@file:Suppress("TooGenericExceptionCaught")
package com.klantenstop.web.pages.admin.categories.manage

import com.klantenstop.web.data.models.dto.requests.categories.InsertCategory
import com.klantenstop.web.data.models.dto.requests.categories.UpdateCategory
import com.klantenstop.web.data.repos.CategoryRepo
import com.materialkobweb.utils.ViewModel
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.w3c.dom.url.URL

class ManageCategoryViewModel(
    private val categoryId: String?,
    private val categoryRepo: CategoryRepo = CategoryRepo
) : ViewModel(), ManageCategoryContract {

    private val _state = MutableStateFlow(ManageCategoryContract.State())
    override val state: StateFlow<ManageCategoryContract.State> = _state.asStateFlow()

    private val _effect = MutableSharedFlow<ManageCategoryContract.Effect>()
    override val effect: SharedFlow<ManageCategoryContract.Effect> = _effect.asSharedFlow()

    init {
        categoryId?.let {
            sendIntent(ManageCategoryContract.Intent.LoadCategory(it))
        }
    }

    override fun sendIntent(intent: ManageCategoryContract.Intent) = coroutineScope.launch {
        when (intent) {
            is ManageCategoryContract.Intent.LoadCategory -> loadCategory(intent.categoryId)
            is ManageCategoryContract.Intent.UpdateName -> updateState { it.copy(name = intent.name) }
            is ManageCategoryContract.Intent.UpdateAltText -> updateState { it.copy(altText = intent.altText) }
            is ManageCategoryContract.Intent.UpdateHighlighted -> updateState { it.copy(highlighted = intent.highlighted) }
            is ManageCategoryContract.Intent.UpdateDescription -> updateState { it.copy(description = intent.description) }
            is ManageCategoryContract.Intent.UpdateLogoFile -> updateState {
                // Revoke previous URL if it exists
                state.value.logoUrl?.let(URL::revokeObjectURL)

                it.copy(
                    logoFile = intent.file,
                    logoUrl = intent.file.let(URL::createObjectURL)
                )
            }

            is ManageCategoryContract.Intent.UploadImage -> uploadImage(intent.file)
            is ManageCategoryContract.Intent.RemoveImage -> removeImage()
            is ManageCategoryContract.Intent.SaveCategory -> saveCategory()
            is ManageCategoryContract.Intent.NavigateBack -> emitEffect(ManageCategoryContract.Effect.NavigateBack)
        }
    }

    override fun emitEffect(effect: ManageCategoryContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (ManageCategoryContract.State) -> ManageCategoryContract.State) {
        _state.update(block)
    }

    private suspend fun loadCategory(id: String) {
        updateState { it.copy(isLoading = true) }
        try {
            val category = categoryRepo.getCategoryById(id)
            updateState {
                it.copy(
                    category = category,
                    name = category.name,
                    altText = category.logo?.altText ?: "",
                    highlighted = category.highlighted,
                    description = category.description,
                    isLoading = false
                )
            }
        } catch (e: Exception) {
            updateState { it.copy(isLoading = false) }
            emitEffect(ManageCategoryContract.Effect.ShowError(e.message ?: "Fout bij het ophalen van categorie"))
            emitEffect(ManageCategoryContract.Effect.NavigateBack)
        }
    }

    private suspend fun saveCategory() {
        val currentState = state.value
        if (!currentState.isValid) {
            emitEffect(ManageCategoryContract.Effect.ShowError("Fout bij het opslaan van categorie, invoer is ongeldig."))
            return
        }

        updateState { it.copy(isSaving = true) }
        try {
            if (categoryId == null) {
                categoryRepo.insertCategory(
                    InsertCategory(
                        name = currentState.name,
                        altText = currentState.altText.takeIf { it.isNotBlank() },
                        highlighted = currentState.highlighted,
                        description = currentState.description
                    ),
                    currentState.logoFile
                )
                emitEffect(ManageCategoryContract.Effect.ShowSuccess("Categorie aangemaakt"))
            } else {
                categoryRepo.updateCategory(
                    categoryId,
                    UpdateCategory(
                        name = currentState.name,
                        altText = currentState.altText.takeIf { it.isNotBlank() },
                        highlighted = currentState.highlighted,
                        description = currentState.description
                    )
                )
                emitEffect(ManageCategoryContract.Effect.ShowSuccess("Categorie bijgewerkt"))
            }
            emitEffect(ManageCategoryContract.Effect.NavigateBack)
        } catch (e: Exception) {
            emitEffect(ManageCategoryContract.Effect.ShowError(e.message ?: "Fout bij het opslaan van categorie"))
        } finally {
            updateState { it.copy(isSaving = false) }
        }
    }

    private suspend fun uploadImage(file: org.w3c.files.File) {
        val id = categoryId ?: return
        updateState { it.copy(isImageLoading = true) }
        try {
            val updated = categoryRepo.updateCategoryImage(id, file, state.value.altText)
            updateState { it.copy(category = updated, isImageLoading = false) }
        } catch (e: Exception) {
            updateState { it.copy(isImageLoading = false) }
            emitEffect(ManageCategoryContract.Effect.ShowError(e.message ?: "Fout bij het uploaden van afbeelding"))
        }
    }

    private suspend fun removeImage() {
        val id = categoryId ?: return
        updateState { it.copy(isImageLoading = true) }
        try {
            val updated = categoryRepo.deleteCategoryImage(id)

            state.value.logoUrl?.let(URL::revokeObjectURL)

            updateState { it.copy(category = updated, isImageLoading = false) }
        } catch (e: Exception) {
            updateState { it.copy(isImageLoading = false) }
            emitEffect(ManageCategoryContract.Effect.ShowError(e.message ?: "Fout bij het verwijderen van afbeelding"))
        }
    }

    override fun dispose() {
        // Revoke URL on cleanup
        state.value.logoUrl?.let(URL::revokeObjectURL)
        super.dispose()
    }
}

